import { Box, Checkbox, FormControlLabel, TextField, SxProps, Typography } from "@mui/material";

interface LegacyOrderDetailsProps {
  sx?: SxProps;
  maxAmount: number | null;
  availableForOrder: boolean;
  allowSpecialRequests: boolean;
  onChangeMaxAmount: (maxAmount: number | null) => void;
  onChangeAvailableForOrder: (availableForOrder: boolean) => void;
  onChangeAllowSpecialRequests: (allowSpecialRequests: boolean) => void;
  variant: "MenuItem" | "MenuItemAppearance" | "RestaurantLinkMenuItemAppearance";
}

/**
 * Legacy Order Details for flag-off / nested dialogs.
 * Explicit row spacing mirrors the production dialog density; do not reuse
 * redesign checkbox sx (p:0 / m:0) here.
 */
export const LegacyOrderDetails = ({
  sx,
  maxAmount,
  availableForOrder,
  allowSpecialRequests,
  variant,
  onChangeMaxAmount,
  onChangeAvailableForOrder,
  onChangeAllowSpecialRequests,
}: LegacyOrderDetailsProps) => {
  const handleSetAvailableForOrder = (nextAvailableForOrder: boolean) => {
    onChangeAvailableForOrder(nextAvailableForOrder);
    if (!nextAvailableForOrder) {
      onChangeAllowSpecialRequests(false);
    }
  };

  const labelSx = {
    alignItems: "center",
    ml: 0,
    // Keep label off the control; default FormControlLabel is tight in a column flex.
    columnGap: 1,
  } as const;

  return (
    <Box sx={[{ display: "flex", flexDirection: "column" }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      <Typography variant="h3" color="textSecondary" sx={{ mb: 1 }}>
        Order Details
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <FormControlLabel
          sx={labelSx}
          label={variant === "MenuItem" ? "Available for Order (Default)" : "Available for Order"}
          disabled={variant === "RestaurantLinkMenuItemAppearance"}
          control={
            <Checkbox
              size="small"
              checked={availableForOrder}
              onChange={e => handleSetAvailableForOrder(e.target.checked)}
            />
          }
        />
        <FormControlLabel
          sx={labelSx}
          label={variant === "MenuItem" ? "Allow Special Requests (Default)" : "Allow Special Requests"}
          control={
            <Checkbox
              size="small"
              checked={allowSpecialRequests}
              onChange={e => onChangeAllowSpecialRequests(e.target.checked)}
              disabled={!availableForOrder}
            />
          }
        />
        <FormControlLabel
          sx={labelSx}
          label={variant === "MenuItem" ? "Default Limit Servings per Order" : "Limit Servings per Order"}
          control={
            <Checkbox
              size="small"
              checked={maxAmount !== null}
              onChange={e => onChangeMaxAmount(e.target.checked ? 1 : null)}
            />
          }
        />
        {maxAmount !== null && (
          <TextField
            label="Max per Order"
            type="number"
            inputProps={{ min: 1 }}
            sx={{ width: 110, height: 48, mt: 0.5 }}
            value={maxAmount}
            onChange={e => onChangeMaxAmount(parseInt(e.target.value))}
            error={isNaN(maxAmount)}
          />
        )}
      </Box>
    </Box>
  );
};
